import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useQuery, gql } from '@apollo/client';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Chip,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Divider,
  alpha,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  FitnessCenter as FitnessCenterIcon,
  EmojiEvents as TrophyIcon,
  WorkspacePremium as MedalIcon,
  CompareArrows as CompareArrowsIcon,
  PublicOutlined as GlobalIcon,
} from '@mui/icons-material';
import DynamicPerformanceChart from './DynamicPerformanceChart';
import PerformanceCompareModal from './PerformanceCompareModal';

// ─── Queries ────────────────────────────────────────────────────────────────
const EXERCISE_PODIUM = gql`
  query ExercisePodium($exerciseId: ID!, $period: RankingPeriod) {
    exercisePodium(exercise_id: $exerciseId, period: $period) {
      first  { id best_value formatted_value medal user { id first_name last_name full_name photo } }
      second { id best_value formatted_value medal user { id first_name last_name full_name photo } }
      third  { id best_value formatted_value medal user { id first_name last_name full_name photo } }
    }
  }
`;

const EXERCISE_NEIGHBORS = gql`
  query ExerciseRankingNeighbors($exerciseId: ID!, $userId: ID, $period: RankingPeriod) {
    exerciseRankingNeighbors(exercise_id: $exerciseId, user_id: $userId, period: $period) {
      rank formatted_value medal user { id first_name last_name full_name photo }
    }
  }
`;

// ─── Constantes ─────────────────────────────────────────────────────────────
const PARAM_CONFIG = {
  TIME:     { color: '#6366f1', label: 'Tiempo'    },
  REPS:     { color: '#ef4444', label: 'Reps'      },
  WEIGHT:   { color: '#f59e0b', label: 'Peso'      },
  DISTANCE: { color: '#10b981', label: 'Distancia' },
};

const MEDAL_CONFIG = {
  gold:   { color: '#FFD700', label: '🥇' },
  silver: { color: '#C0C0C0', label: '🥈' },
  bronze: { color: '#CD7F32', label: '🥉' },
};

// ─── Helpers ────────────────────────────────────────────────────────────────
function formatParamValue(value, paramType, unit) {
  if (!value && value !== 0) return '—';
  switch (paramType) {
    case 'TIME': {
      const m = Math.floor(value / 60);
      const s = value % 60;
      return m > 0 ? `${m}m ${s}s` : `${s}s`;
    }
    case 'WEIGHT':   return `${value} ${unit || 'kg'}`;
    case 'DISTANCE': return `${value} ${unit || 'km'}`;
    case 'REPS':     return `${value} reps`;
    default:         return String(value);
  }
}

function aggregateSets(sets, paramType) {
  if (!sets?.length) return 0;
  switch (paramType) {
    case 'TIME':     return sets.reduce((s, x) => s + (x.time_seconds  || 0), 0);
    case 'REPS':     return sets.reduce((s, x) => s + (x.repetitions   || 0), 0);
    case 'DISTANCE': return sets.reduce((s, x) => s + (x.distance_km   || 0), 0);
    case 'WEIGHT': {
      const vals = sets.map((x) => x.weight_kg).filter(Boolean);
      return vals.length
        ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10
        : 0;
    }
    default: return 0;
  }
}

function formatDate(dateStr) {
  try { return format(parseISO(dateStr), 'd MMM yyyy', { locale: es }); }
  catch { return dateStr; }
}

// ─── Sub-componente Podio ────────────────────────────────────────────────────
function PodiumEntry({ entry = null, medalKey, highlightUserId = null, selfUserId = null }) {
  if (!entry) return null;
  const medal = MEDAL_CONFIG[medalKey];
  const isHighlighted = String(entry.user?.id) === String(highlightUserId);
  const isSelf        = isHighlighted && String(highlightUserId) === String(selfUserId);

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2, flex: 1, minWidth: 0,
      bgcolor: isHighlighted ? alpha('#F9BD4E', 0.08) : 'rgba(128,128,128,0.08)',
      border: `1px solid ${isHighlighted ? alpha('#F9BD4E', 0.3) : 'rgba(128,128,128,0.22)'}`,
    }}>
      <Typography sx={{ fontSize: '1.4rem', lineHeight: 1 }}>{medal.label}</Typography>
      <Avatar src={entry.user?.photo}
        sx={{ width: 32, height: 32, fontSize: '0.75rem', border: `2px solid ${medal.color}` }}>
        {entry.user?.first_name?.[0]}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" noWrap
          sx={{ color: isHighlighted ? 'gold.main' : 'text.primary', fontWeight: isHighlighted ? 700 : 500, display: 'block' }}>
          {entry.user?.first_name} {entry.user?.last_name}{isSelf && ' (tú)'}
        </Typography>
        <Typography variant="caption" sx={{ color: medal.color, fontWeight: 700 }}>
          {entry.formatted_value}
        </Typography>
      </Box>
    </Box>
  );
}

PodiumEntry.propTypes = {
  entry: PropTypes.shape({
    user: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      photo: PropTypes.string,
    }),
    formatted_value: PropTypes.string,
  }),
  medalKey: PropTypes.oneOf(['gold', 'silver', 'bronze']).isRequired,
  highlightUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selfUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// ─── Sub-componente fila de vecindario (posición del cliente ±1) ──────────────
function NeighborRow({ entry, highlightUserId = null, selfUserId = null }) {
  const isHighlighted = String(entry.user?.id) === String(highlightUserId);
  const isSelf        = isHighlighted && String(highlightUserId) === String(selfUserId);

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, px: 1.5, borderRadius: 2,
      bgcolor: isHighlighted ? alpha('#F9BD4E', 0.1) : 'transparent',
      border: `1px solid ${isHighlighted ? alpha('#F9BD4E', 0.35) : 'transparent'}`,
    }}>
      <Typography sx={{
        minWidth: 34, textAlign: 'center', fontWeight: 800, fontVariantNumeric: 'tabular-nums',
        color: isHighlighted ? 'gold.main' : 'text.secondary',
      }}>
        #{entry.rank}
      </Typography>
      <Avatar src={entry.user?.photo} sx={{ width: 34, height: 34, fontSize: '0.8rem' }}>
        {entry.user?.first_name?.[0]}
      </Avatar>
      <Typography noWrap sx={{
        flex: 1, minWidth: 0,
        color: isHighlighted ? 'gold.main' : 'text.primary',
        fontWeight: isHighlighted ? 700 : 500,
      }}>
        {entry.user?.full_name || `${entry.user?.first_name || ''} ${entry.user?.last_name || ''}`}
        {isSelf && ' (tú)'}
      </Typography>
      <Typography sx={{ color: isHighlighted ? 'gold.main' : 'text.secondary', fontWeight: 700 }}>
        {entry.formatted_value}
      </Typography>
    </Box>
  );
}

NeighborRow.propTypes = {
  entry: PropTypes.shape({
    rank: PropTypes.number,
    formatted_value: PropTypes.string,
    user: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      full_name: PropTypes.string,
      photo: PropTypes.string,
    }),
  }).isRequired,
  highlightUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selfUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// ─── Chip OTRIXVERSE (mismo estilo que en Ejercicios) ────────────────────────
function OtrixverseChip() {
  return (
    <Chip
      size="small"
      icon={<GlobalIcon sx={{ fontSize: 14 }} />}
      label="OTRIXVERSE"
      sx={{ bgcolor: 'gold.soft', color: 'gold.main', fontWeight: 700, height: 20, '& .MuiChip-icon': { color: 'gold.main' } }}
    />
  );
}

// ─── Historial de sesiones (tabla en desktop, tarjetas apiladas en móvil) ─────
// En móvil evitamos la tabla ancha (scroll horizontal feo): cada sesión se apila como tarjeta.
function SessionHistory({ sortedPerformances, params, paramTypes, stacked }) {
  if (stacked) {
    return (
      <Stack spacing={1.5}>
        {sortedPerformances.map((perf) => (
          <Box key={perf.id} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'hairline.main', bgcolor: 'rgba(0,0,0,0.2)', p: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>
                {formatDate(perf.period_start_date)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {perf.sets?.length || 0} sets
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: perf.trainer ? 1 : 0 }}>
              {paramTypes.map((pt) => {
                const unit = params.find((p) => p.parameter_type === pt)?.unit;
                const cfg = PARAM_CONFIG[pt] || {};
                return (
                  <Box key={pt} sx={{ px: 1, py: 0.5, borderRadius: 1.5, bgcolor: alpha(cfg.color || '#888', 0.12), border: `1px solid ${alpha(cfg.color || '#888', 0.25)}` }}>
                    <Typography variant="caption" sx={{ color: cfg.color || 'text.secondary', fontWeight: 600 }}>
                      {cfg.label || pt}:{' '}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 700 }}>
                      {formatParamValue(aggregateSets(perf.sets, pt), pt, unit)}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
            {perf.trainer && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar src={perf.trainer.photo} sx={{ width: 20, height: 20, fontSize: '0.6rem' }}>
                  {perf.trainer.first_name?.[0]}
                </Avatar>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {perf.trainer.first_name} {perf.trainer.last_name}
                </Typography>
              </Stack>
            )}
          </Box>
        ))}
      </Stack>
    );
  }

  return (
    <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'hairline.main', bgcolor: 'rgba(0,0,0,0.2)' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: 'text.secondary', borderColor: 'hairline.main', fontSize: '0.75rem' }}>Fecha</TableCell>
            <TableCell sx={{ color: 'text.secondary', borderColor: 'hairline.main', fontSize: '0.75rem' }}>Sets</TableCell>
            {paramTypes.map((pt) => (
              <TableCell key={pt} sx={{ color: PARAM_CONFIG[pt]?.color || 'rgba(255,255,255,0.5)', borderColor: 'hairline.main', fontSize: '0.75rem' }}>
                {PARAM_CONFIG[pt]?.label || pt}
              </TableCell>
            ))}
            <TableCell sx={{ color: 'text.secondary', borderColor: 'hairline.main', fontSize: '0.75rem' }}>Entrenador</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedPerformances.map((perf) => (
            <TableRow key={perf.id}
              sx={{ '&:last-child td': { border: 0 }, '&:hover': { bgcolor: 'rgba(249,189,78,0.04)' } }}>
              <TableCell sx={{ color: 'text.primary', borderColor: 'hairline.main', fontSize: '0.8rem' }}>
                {formatDate(perf.period_start_date)}
              </TableCell>
              <TableCell sx={{ color: 'text.secondary', borderColor: 'hairline.main', fontSize: '0.8rem' }}>
                {perf.sets?.length || 0}
              </TableCell>
              {paramTypes.map((pt) => {
                const unit = params.find((p) => p.parameter_type === pt)?.unit;
                return (
                  <TableCell key={pt} sx={{ color: 'white', fontWeight: 600, borderColor: 'hairline.main', fontSize: '0.8rem' }}>
                    {formatParamValue(aggregateSets(perf.sets, pt), pt, unit)}
                  </TableCell>
                );
              })}
              <TableCell sx={{ borderColor: 'hairline.main' }}>
                {perf.trainer ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar src={perf.trainer.photo} sx={{ width: 22, height: 22, fontSize: '0.65rem' }}>
                      {perf.trainer.first_name?.[0]}
                    </Avatar>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {perf.trainer.first_name} {perf.trainer.last_name}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

SessionHistory.propTypes = {
  sortedPerformances: PropTypes.array.isRequired,
  params: PropTypes.array.isRequired,
  paramTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  stacked: PropTypes.bool.isRequired,
};

// ─── Componente principal ────────────────────────────────────────────────────
function DynamicExerciseGroup({ group, profileUserId = null }) {
  const { exercise, performances } = group;
  const totalCount = group.total_count;
  const params        = exercise.parameters || [];
  const paramTypes    = params.map((p) => p.parameter_type);
  const currentUserId = useSelector((state) => state.auth.user?.id);
  // El ranking se muestra relativo al dueño del perfil (el cliente que se está viendo),
  // no al usuario logueado. Si no se pasa, cae al usuario actual.
  const rankingUserId = profileUserId ?? currentUserId;

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [detailOpen, setDetailOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const canCompare = performances.length >= 2;
  const otrixverse = exercise.is_otrixverse;

  const sortedPerformances = useMemo(() =>
    [...performances].sort((a, b) => new Date(b.period_start_date) - new Date(a.period_start_date)),
    [performances]
  );

  // Ranking de este ejercicio dinámico
  const { data: podiumData } = useQuery(EXERCISE_PODIUM, {
    variables: { exerciseId: exercise.id, period: 'ALL_TIME' },
    fetchPolicy: 'cache-first',
  });
  const { data: neighborsData } = useQuery(EXERCISE_NEIGHBORS, {
    variables: { exerciseId: exercise.id, userId: rankingUserId, period: 'ALL_TIME' },
    fetchPolicy: 'cache-first',
    skip: !rankingUserId,
  });

  const podium        = podiumData?.exercisePodium;
  const neighbors     = neighborsData?.exerciseRankingNeighbors || [];
  const myRank        = neighbors.find((n) => String(n.user?.id) === String(rankingUserId)) || null;
  const isSelfProfile = String(rankingUserId) === String(currentUserId);

  return (
    <>
      {/* ── Card resumen — al tocarla se abre la vista de detalle ── */}
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setDetailOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailOpen(true); } }}
        sx={{
          display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer',
          px: { xs: 2, sm: 3 }, py: 1.75, mb: 2,
          backgroundColor: 'surface.content', border: '1px solid', borderColor: 'hairline.main',
          borderRadius: '12px',
          transition: 'transform 0.2s cubic-bezier(0.32,0.72,0,1), border-color 0.2s cubic-bezier(0.32,0.72,0,1)',
          '&:hover': { transform: 'translateY(-2px)', borderColor: 'rgba(249,189,78,0.35)' },
          '&:focus-visible': { outline: '2px solid var(--otx-focus-ring)', outlineOffset: 2 },
        }}
      >
        <Avatar sx={{ bgcolor: alpha('#F9BD4E', 0.15), border: '1px solid rgba(249,189,78,0.3)', width: 44, height: 44 }}>
          <FitnessCenterIcon sx={{ color: '#F9BD4E', fontSize: 22 }} />
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }} noWrap>
              {exercise.name}
            </Typography>
            {otrixverse && <OtrixverseChip />}
          </Stack>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {params.map((param) => {
              const cfg = PARAM_CONFIG[param.parameter_type] || {};
              return (
                <Chip key={param.id} label={cfg.label || param.parameter_type} size="small"
                  sx={{ bgcolor: alpha(cfg.color || '#888', 0.15), color: cfg.color || '#888',
                        border: `1px solid ${alpha(cfg.color || '#888', 0.3)}`, fontSize: '0.7rem', height: 20 }} />
              );
            })}
          </Stack>
        </Box>

        {/* Posición en el ranking del ejercicio */}
        {myRank && (
          <Tooltip title={`Mejor marca: ${myRank.formatted_value}`}>
            <Box sx={{ textAlign: 'center', mr: { xs: 0, sm: 1 }, display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                {isSelfProfile ? 'mi posición' : 'posición'}
              </Typography>
              <Chip
                label={`#${myRank.rank}`}
                size="small"
                icon={<MedalIcon sx={{ fontSize: '14px !important' }} />}
                sx={{ bgcolor: alpha('#F9BD4E', 0.15), color: '#F9BD4E',
                      border: '1px solid rgba(249,189,78,0.4)', fontWeight: 700 }}
              />
            </Box>
          </Tooltip>
        )}

        {/* Contador sesiones */}
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h5" sx={{ color: '#F9BD4E', fontWeight: 800, lineHeight: 1 }}>{totalCount}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>sesiones</Typography>
        </Box>

        <ChevronRightIcon sx={{ color: 'text.secondary' }} />
      </Box>

      {/* ── Vista de detalle (full-screen en móvil, diálogo grande en desktop) ── */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        fullScreen={fullScreen}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: {
          backgroundColor: 'surface.content', backgroundImage: 'none',
          ...(fullScreen ? {} : { borderRadius: 3, border: '1px solid', borderColor: 'hairline.strong' }),
        } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 6 }}>
          <Avatar sx={{ bgcolor: alpha('#F9BD4E', 0.15), border: '1px solid rgba(249,189,78,0.3)', width: 40, height: 40 }}>
            <FitnessCenterIcon sx={{ color: '#F9BD4E', fontSize: 20 }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h6" sx={{ fontWeight: 800 }} noWrap>{exercise.name}</Typography>
              {otrixverse && <OtrixverseChip />}
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {totalCount} {totalCount === 1 ? 'sesión' : 'sesiones'}
            </Typography>
          </Box>
          <IconButton onClick={() => setDetailOpen(false)} size="small" sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Comparar dos sesiones del mismo ejercicio */}
          {canCompare && (
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CompareArrowsIcon />}
                onClick={() => setCompareOpen(true)}
                sx={{
                  color: 'gold.main', borderColor: 'rgba(249,189,78,0.4)', textTransform: 'none', fontWeight: 700,
                  '&:hover': { borderColor: 'gold.main', bgcolor: 'rgba(249,189,78,0.08)' },
                }}
              >
                Comparar sesiones
              </Button>
            </Stack>
          )}

          {/* Vecindario: la posición del cliente con quien tiene justo encima y justo debajo */}
          {neighbors.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <MedalIcon sx={{ color: '#F9BD4E', fontSize: 18 }} />
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  {isSelfProfile ? 'Tu posición' : 'Posición del cliente'}
                </Typography>
              </Stack>
              <Box sx={{ borderRadius: 2, border: '1px solid', borderColor: 'hairline.main', bgcolor: 'rgba(0,0,0,0.2)', p: 0.5 }}>
                {neighbors.map((entry) => (
                  <NeighborRow key={entry.user?.id} entry={entry} highlightUserId={rankingUserId} selfUserId={currentUserId} />
                ))}
              </Box>
            </Box>
          )}

          {/* Podio del ejercicio dinámico */}
          {podium && (podium.first || podium.second || podium.third) && (
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <TrophyIcon sx={{ color: '#F9BD4E', fontSize: 18 }} />
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Top 3 en este ejercicio
                </Typography>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                {podium.first  && <PodiumEntry entry={podium.first}  medalKey="gold"   highlightUserId={rankingUserId} selfUserId={currentUserId} />}
                {podium.second && <PodiumEntry entry={podium.second} medalKey="silver" highlightUserId={rankingUserId} selfUserId={currentUserId} />}
                {podium.third  && <PodiumEntry entry={podium.third}  medalKey="bronze" highlightUserId={rankingUserId} selfUserId={currentUserId} />}
              </Stack>
            </Box>
          )}

          <Divider sx={{ borderColor: 'hairline.main', mb: 3 }} />

          {/* Gráfica de progreso — se alimenta de las mismas sesiones del historial */}
          {performances.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <DynamicPerformanceChart performances={performances} params={params} />
            </Box>
          )}

          {/* Historial (tabla en desktop, tarjetas apiladas en móvil) */}
          <SessionHistory
            sortedPerformances={sortedPerformances}
            params={params}
            paramTypes={paramTypes}
            stacked={fullScreen}
          />
        </DialogContent>
      </Dialog>

      {canCompare && (
        <PerformanceCompareModal
          open={compareOpen}
          onClose={() => setCompareOpen(false)}
          performances={performances}
          params={params}
          exerciseName={exercise.name}
        />
      )}
    </>
  );
}

DynamicExerciseGroup.propTypes = {
  group: PropTypes.shape({
    exercise: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      is_otrixverse: PropTypes.bool,
      parameters: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        parameter_type: PropTypes.string.isRequired,
        unit: PropTypes.string,
      })),
    }).isRequired,
    performances: PropTypes.array.isRequired,
    total_count: PropTypes.number.isRequired,
  }).isRequired,
  profileUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default DynamicExerciseGroup;
